import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import { adminApi } from '../services/adminApi';
import './AdminUsers.css';

interface User {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  isActive: boolean;
  createdAt: string;
  lastLogin?: string;
  orders?: {
    totalOrders: number;
    totalSpent: number;
  };
  address?: {
    street?: string;
    city?: string;
    postalCode?: string;
    country?: string;
  };
}

const AdminUsers: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showUserDetails, setShowUserDetails] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await adminApi.getUsers();
      
      // Handle different API response formats
      let usersData = [];
      if (Array.isArray(data)) {
        usersData = data;
      } else if (data && Array.isArray(data.users)) {
        usersData = data.users;
      } else if (data && Array.isArray(data.data)) {
        usersData = data.data;
      } else {
        setUsers([]);
        return;
      }
      
      // If orders property is missing, let's fetch real order data for all users
      if (usersData.length > 0 && !usersData[0].orders) {
        
        try {
          // Fetch all orders
          const ordersResponse = await adminApi.getOrders({ limit: 1000 });
          const allOrders = Array.isArray(ordersResponse) ? ordersResponse : ordersResponse.orders || [];
          
          // Calculate statistics for each user
          const usersWithRealStats = usersData.map((user: any) => {
            const userOrders = allOrders.filter((order: any) => order.user?.id === user.id || order.userId === user.id);
            const totalOrders = userOrders.length;
            const totalSpent = userOrders.reduce((sum: number, order: any) => sum + (Number(order.totalAmount) || 0), 0);
            
            return {
              ...user,
              orders: {
                totalOrders,
                totalSpent
              }
            };
          });
          
          setUsers(usersWithRealStats);
        } catch (error) {
          // Fallback to empty stats if fetching fails
          const usersWithEmptyOrders = usersData.map((user: any) => ({
            ...user,
            orders: {
              totalOrders: 0,
              totalSpent: 0
            }
          }));
          setUsers(usersWithEmptyOrders);
        }
      } else {
        setUsers(usersData);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleUserStatus = async (userId: number, isActive: boolean) => {
    try {
      await adminApi.updateUserStatus(userId, isActive);
      await loadUsers();
    } catch (err: any) {
      setError(err.message || 'Failed to update user status');
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = searchTerm === '' || 
      user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && user.isActive) ||
      (statusFilter === 'inactive' && !user.isActive);
    
    return matchesSearch && matchesStatus;
  });

  const fetchUserOrderStats = async (userId: number) => {
    try {
      // Fetch user's orders using the admin orders API
      const ordersResponse = await adminApi.getOrders({ limit: 1000 }); // Get all orders
      const allOrders = Array.isArray(ordersResponse) ? ordersResponse : ordersResponse.orders || [];
      
      // Filter orders for this specific user
      const userOrders = allOrders.filter((order: any) => order.user?.id === userId || order.userId === userId);
      
      const totalOrders = userOrders.length;
      const totalSpent = userOrders.reduce((sum: number, order: any) => sum + (Number(order.totalAmount) || 0), 0);
      
      return { totalOrders, totalSpent };
    } catch (error) {
      return { totalOrders: 0, totalSpent: 0 };
    }
  };

  const handleViewUser = async (user: User) => {
    // If user doesn't have order statistics, fetch them
    if (!user.orders || (user.orders.totalOrders === 0 && user.orders.totalSpent === 0)) {
      const orderStats = await fetchUserOrderStats(user.id);
      const userWithStats = {
        ...user,
        orders: orderStats
      };
      setSelectedUser(userWithStats);
    } else {
      setSelectedUser(user);
    }
    setShowUserDetails(true);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-loading">
          <div className="loading-spinner"></div>
          <p>Loading Users...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-users">
        <div className="users-header">
          <div className="header-content">
            <h1>Users Management</h1>
            <p>Manage customer accounts and information</p>
          </div>
          <div className="header-actions">
            <button className="btn btn-secondary" onClick={loadUsers}>
              <span className="btn-icon">🔄</span>
              Refresh
            </button>
          </div>
        </div>

        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            {error}
            <button onClick={() => setError('')} className="close-error">×</button>
          </div>
        )}

        <div className="users-filters">
          <div className="search-group">
            <input
              type="text"
              placeholder="Search users by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          <div className="filter-group">
            <label>Status:</label>
            <select 
              value={statusFilter} 
              onChange={(e) => setStatusFilter(e.target.value)}
              className="status-filter"
            >
              <option value="all">All Users</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div className="users-count">
            <span>{filteredUsers.length} users found</span>
          </div>
        </div>

        <div className="users-grid">
          {filteredUsers.map((user) => (
            <div key={user.id} className="user-card">
              <div className="user-avatar">
                <span>{user.firstName.charAt(0)}{user.lastName.charAt(0)}</span>
              </div>
              
              <div className="users-info">
                <div className="user-name">
                  {user.firstName} {user.lastName}
                </div>
                <div className="user-email">{user.email}</div>
                {user.phone && (
                  <div className="user-phone">{user.phone}</div>
                )}
              </div>

              <div className="user-stats">
                <div className="stat-item">
                  <span className="stat-value">{user.orders?.totalOrders || 0}</span>
                  <span className="stat-label">Orders</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">€{user.orders?.totalSpent?.toFixed(2) || '0.00'}</span>
                  <span className="stat-label">Spent</span>
                </div>
              </div>

              <div className="user-meta">
                <div className="join-date">
                  Joined: {new Date(user.createdAt).toLocaleDateString('el-GR')}
                </div>
                {user.lastLogin && (
                  <div className="last-login">
                    Last login: {new Date(user.lastLogin).toLocaleDateString('el-GR')}
                  </div>
                )}
              </div>

              <div className="user-actions">
                <button 
                  className="btn btn-primary view-details-btn"
                  onClick={() => handleViewUser(user)}
                >
                  View Details
                </button>
              </div>
            </div>
          ))}
        </div>

        {filteredUsers.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">👥</div>
            <h3>No Users Found</h3>
            <p>No users match the current search and filter criteria</p>
          </div>
        )}

        {/* User Details Modal */}
        {showUserDetails && selectedUser && (
          <UserDetailsModal
            user={selectedUser}
            onClose={() => {
              setShowUserDetails(false);
              setSelectedUser(null);
            }}
          />
        )}
      </div>
    </AdminLayout>
  );
};

interface UserDetailsModalProps {
  user: User;
  onClose: () => void;
}

const UserDetailsModal: React.FC<UserDetailsModalProps> = ({ user, onClose }) => {
  
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content user-details-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>User Details</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="user-details-content">
          <div className="details-header">
            <div className="user-avatar-large">
              <span>{user.firstName.charAt(0)}{user.lastName.charAt(0)}</span>
            </div>
            <div className="user-title">
              <h3>{user.firstName} {user.lastName}</h3>
              <p>{user.email}</p>
            </div>
          </div>

          <div className="details-grid">
            <div className="detail-section">
              <h4>Contact Information</h4>
              <div className="detail-info">
                <p><strong>Email:</strong> {user.email}</p>
                {user.phone && (
                  <p><strong>Phone:</strong> {user.phone}</p>
                )}
              </div>
            </div>

            <div className="detail-section">
              <h4>Account Information</h4>
              <div className="detail-info">
                <p><strong>Account Created:</strong> {new Date(user.createdAt).toLocaleString('el-GR')}</p>
                {user.lastLogin && (
                  <p><strong>Last Login:</strong> {new Date(user.lastLogin).toLocaleString('el-GR')}</p>
                )}
              </div>
            </div>

            {user.address && (
              <div className="detail-section full-width">
                <h4>Address Information</h4>
                <div className="detail-info">
                  {user.address.street && <p>{user.address.street}</p>}
                  {user.address.city && user.address.postalCode && (
                    <p>{user.address.city}, {user.address.postalCode}</p>
                  )}
                  {user.address.country && <p>{user.address.country}</p>}
                </div>
              </div>
            )}

            <div className="detail-section full-width">
              <h4>Order Statistics</h4>
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-content">
                    <div className="stat-number">{user.orders?.totalOrders || 0}</div>
                    <div className="stat-title">Total Orders</div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-content">
                    <div className="stat-number">€{user.orders?.totalSpent?.toFixed(2) || '0.00'}</div>
                    <div className="stat-title">Total Spent</div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-content">
                    <div className="stat-number">
                      €{user.orders?.totalOrders ? (user.orders.totalSpent / user.orders.totalOrders).toFixed(2) : '0.00'}
                    </div>
                    <div className="stat-title">Avg Order Value</div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminUsers;